import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import styles from "../Reviewd/Reviewd.module.scss"
import { faCirclePlay, faComment } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Dialog, IconButton, DialogContent, DialogTitle, Button, Pagination, Stack, Box, CircularProgress } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close';
import { toast, ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import CoachFeedback from '../CoachFeedback';
import { getFetchGamePostFileThumbnail, getVideos } from '../../../redux/network/videoApi';
import { useDispatch, useSelector } from 'react-redux';
import thumbnail from './thumbnail.jpg';
import { useNavigate } from 'react-router-dom';
import SearchVideos from "../../SearchBoxes/SearchVideos";
import { SEARCH_BOX } from "../../../constant"
import SearchUploadedVideoPagination from '../../Pagination/SearchUploadedVideoPagination/SearchUploadedVideoPagination';
import CustomDropdown from '../../CustomeDropdown/CustomDropdown';
import { usePagination } from '../../../hooks/usePagination';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';

// Set limit to 12
const VIDEO_SEARCH_API_LIMIT = 12;

// VideoJS deprecation warnings are suppressed globally in utils/videojsWarnings.js

const dataFormatter = (dateString) => {
    const inputDate = new Date(dateString);

    const monthNames = [
        "January", "February", "March",
        "April", "May", "June", "July",
        "August", "September", "October",
        "November", "December"
    ];

    const day = inputDate.getDate();
    const monthIndex = inputDate.getMonth();
    const year = inputDate.getFullYear();
    const hours = inputDate.getHours();
    const minutes = inputDate.getMinutes();

    return `${day} ${monthNames[monthIndex]} ${year} ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}
const extractTitle = (videoPath) => {
    if (!videoPath) return "Unknown Title";
    const filename = videoPath.split('/').pop().replace(/\.[^/.]+$/, "");
    return filename
};
const truncateTitle = (filename) => {
    if (!filename) return "";
    const words = filename.split(" ");
    return words.length > 6 ? words.slice(0, 6).join(" ") + " .." : filename;
};

const extractPlayerName = (video) => {
    if (!video || !video.poster || !video.poster.profileData || !video.poster.profileData.name) {
        return "Unknown";  // Handle missing values safely
    }

    const { firstName = "", lastName = "" } = video.poster.profileData.name;
    return `${firstName}  ${lastName}`.trim();
};

// Helper function to calculate the start and end of time ranges
const getTimeRange = (range) => {
    const now = new Date();
    let start, end;
    switch (range) {
        case 'lastOneWeek':
            start = new Date(now.setDate(now.getDate() - 7));
            end = new Date();
            break;
        case 'lastMonth':
            start = new Date(now.setMonth(now.getMonth() - 1));
            end = new Date();
            break;
        case 'last3Months':
            start = new Date(now.setMonth(now.getMonth() - 3));
            end = new Date();
            break;
        default:
            start = null;
            end = null;
    }
    return { start: start?.getTime(), end: end?.getTime() };
};

const sortOptions = [
    { value: 'feedbackPriority', label: 'Sort By' },
    { value: 'feedbackPriority', label: 'Feedback Priority' },
    { value: 'newToOld', label: 'New to Old' },
    { value: 'oldToNew', label: 'Old to New' },
];

const Reviewd = () => {
    const env = process.env.REACT_APP_ENV;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    // Redux selectors
    const { isStarted, isError, error, data, totalNumberOfRecords } = useSelector(state => state.video.video);

    // Custom pagination hook
    const {
        currentPage,
        totalPages,
        totalRecords,
        searchTerm,
        sortOption,
        goToPage,
        updateSearchTerm,
        updateSortOption
    } = usePagination('reviewed');

    // Local state
    const [videoThumbnails, setVideoThumbnails] = useState({});
    const [thumbnailLoading, setThumbnailLoading] = useState({});

    // Debug tracking
    const prevApiPayload = useRef(null);
    const thumbnailsLoadedRef = useRef(false);

    // Derived state
    const jwtToken = localStorage.getItem("BQS_TOKEN");
    const userId = localStorage.getItem("BQS__ID");

    // Get sort configuration - memoized to prevent unnecessary re-renders
    const sortConfig = useMemo(() => {
        switch (sortOption) {
            case 'feedbackPriority':
                return {
                    "coachManualFeedback": 1, // Empty feedback first (1 = ascending, empty strings come first)
                    "updatedAt": -1
                };
            case 'newToOld':
                return { "updatedAt": -1 };
            case 'oldToNew':
                return { "updatedAt": 1 };
            default:
                return {
                    "coachManualFeedback": 1, // Default: Empty feedback first
                    "updatedAt": -1
                };
        }
    }, [sortOption]);

    // Memoize API payload to prevent unnecessary API calls
    const apiPayload = useMemo(() => {
        if (!jwtToken || !userId || env === "standalone") {
            return null;
        }

        return {
            "condition": {
                "state": ["reviewed"],
                "searchTerm": searchTerm,
                "poster": userId
            },
            "sort": sortConfig,
            "skip": currentPage - 1,
            "limit": VIDEO_SEARCH_API_LIMIT,
        };
    }, [jwtToken, userId, searchTerm, sortConfig, currentPage, env]);

    // Fetch videos - only when apiPayload changes
    useEffect(() => {
        if (apiPayload) {
            dispatch(getVideos(jwtToken, apiPayload));
        }
    }, [apiPayload, jwtToken, dispatch]);

    // Update pagination state when video data changes - optimized
    useEffect(() => {
        if (totalNumberOfRecords !== undefined) {
            const totalRecords = totalNumberOfRecords;
            const totalPages = Math.ceil(totalRecords / VIDEO_SEARCH_API_LIMIT);

            // Update Redux state directly without using the hook function
            dispatch({
                type: 'pagination/updatePaginationFromResponse',
                payload: {
                    totalRecords,
                    currentPage,
                    searchTerm,
                    sortOption,
                    component: 'reviewed'
                }
            });
        }
    }, [totalNumberOfRecords, currentPage, searchTerm, sortOption, dispatch]);

    // Scroll to top when page changes
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentPage]);

    // Load thumbnails - optimized to prevent infinite loops
    useEffect(() => {
        if (data?.length > 0 && jwtToken && env !== "standalone") {
            // Check if we've already loaded thumbnails for this data
            const dataHash = JSON.stringify(data.map(v => v._id));
            if (thumbnailsLoadedRef.current === dataHash) {
                return;
            }

            const loadThumbnails = async () => {
                // Only load thumbnails for videos that don't already have them
                const videosNeedingThumbnails = data.filter(video => !videoThumbnails[video._id]);

                if (videosNeedingThumbnails.length === 0) {
                    thumbnailsLoadedRef.current = dataHash;
                    return;
                }

                const loadingStates = {};
                const newThumbnails = { ...videoThumbnails };

                // Set loading states for videos that need thumbnails
                videosNeedingThumbnails.forEach(video => {
                    loadingStates[video._id] = true;
                });
                setThumbnailLoading(prev => ({ ...prev, ...loadingStates }));

                // Fetch thumbnails in parallel
                const thumbnailPromises = videosNeedingThumbnails.map(async (video) => {
                    try {
                        const thumbnailUrl = await getFetchGamePostFileThumbnail(
                            jwtToken,
                            video._id,
                            video.game[0]?.tnPath
                        );
                        return { id: video._id, url: thumbnailUrl };
                    } catch (error) {
                        return { id: video._id, url: null };
                    }
                });

                const results = await Promise.all(thumbnailPromises);

                // Update thumbnails
                results.forEach(({ id, url }) => {
                    if (url) {
                        newThumbnails[id] = url;
                    }
                });

                setVideoThumbnails(newThumbnails);

                // Mark as loaded for this data
                thumbnailsLoadedRef.current = dataHash;

                // Clear loading states
                const clearLoadingStates = {};
                Object.keys(loadingStates).forEach(id => {
                    clearLoadingStates[id] = false;
                });
                setThumbnailLoading(prev => ({ ...prev, ...clearLoadingStates }));
            };

            loadThumbnails();
        }
    }, [data, jwtToken, env]); // Removed videoThumbnails from dependencies to prevent infinite loop

    // Handle video click - memoized to prevent unnecessary re-renders
    const handlePlayClick = useCallback((video) => {
        try { ReactGA.event({ category: 'CoachFlow', action: 'reviewed_play_click' }); } catch { }
        // Store essential video data for CreateFeedback
        localStorage.setItem("reviewPostId", video._id);
        localStorage.setItem(`${video._id}_reviewPost`, JSON.stringify(video));
        localStorage.setItem("videoLinkInObjectStore", video.game[0].path);

        // Store user data
        if (env !== "standalone") {
            const { firstName, lastName } = video?.poster?.profileData?.name || {};
            localStorage.setItem("firstname", firstName || "");
            localStorage.setItem("lastname", lastName || "");
        }

        // Navigate to CreateFeedback
        navigate(`/coach_flow/reviewd-video/feedback/${video._id}`, {
            state: {
                videoTags: video.tags,
                aiFeedbackState: video?.aiFeedbackState || "created"
            }
        });
    }, [env, navigate]);

    // GA-wrapped handlers
    const handleSearchChange = (text) => {
        try { ReactGA.event({ category: 'CoachFlow', action: 'reviewed_search_change', label: text ? 'non_empty' : 'empty' }); } catch { }
        updateSearchTerm(text);
    };

    const handleSortChange = (val) => {
        try { ReactGA.event({ category: 'CoachFlow', action: 'reviewed_sort_change', label: val }); } catch { }
        updateSortOption(val);
    };

    const handlePageChange = (page) => {
        try { ReactGA.event({ category: 'CoachFlow', action: 'reviewed_page_change', label: String(page) }); } catch { }
        goToPage(page);
    };

    // Filter videos for standalone mode - memoized to prevent unnecessary recalculations
    const filteredVideos = useMemo(() => {
        return env === "standalone"
            ? data?.filter(video => video.state === "reviewed") || []
            : data || [];
    }, [env, data]);






    return (
        <div className="w-full pb-10 pt-24">
            {/* Header Section */}
            <div className="flex flex-col gap-6 mb-8">
                <div>
                    <h1 className="text-4xl font-extrabold text-[#15803d]">My Reviews</h1>
                </div>

                <div className="flex flex-col md:flex-row items-center gap-4 w-full">
                    <div className="flex-1 w-full">
                        <SearchVideos
                            setSearchText={handleSearchChange}
                            existingSearchText={searchTerm}
                            placeholderText={SEARCH_BOX.VIDEO_SEARCH.COACH_PERSPECTIVE.PACEHOLDER_TEXT}
                            className="w-full"
                        />
                    </div>
                    <div className="w-full md:w-48 shrink-0">
                        <CustomDropdown
                            options={sortOptions}
                            selectedValue={sortOption}
                            onChange={handleSortChange}
                            label=""
                        />
                    </div>
                </div>
            </div>

            {/* Video Grid Section */}
            <div>
                {isStarted ? (
                    <Box sx={{ display: 'flex', justifyContent: "center", alignItems: "center", height: "50vh" }}>
                        <CircularProgress sx={{ color: '#10b981' }} />
                    </Box>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {filteredVideos.length > 0 ? (
                            filteredVideos.map((video) => {
                                const thumbnailUrl = videoThumbnails[video._id] || thumbnail;
                                const isLoading = thumbnailLoading[video._id];

                                return (
                                    <div
                                        key={video._id}
                                        className="group relative bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden border border-slate-100 flex flex-col"
                                        onClick={() => handlePlayClick(video)}
                                    >
                                        {/* Thumbnail Container */}
                                        <div className="relative aspect-video overflow-hidden bg-slate-100">
                                            <div
                                                className="w-full h-full bg-cover bg-center transform group-hover:scale-105 transition-transform duration-500"
                                                style={{ backgroundImage: `url(${thumbnailUrl})` }}
                                            />

                                            {/* Gradient Overlay */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300" />

                                            {/* Play Icon Overlay */}
                                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-90 group-hover:scale-100">
                                                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/50">
                                                    <FontAwesomeIcon icon={faCirclePlay} className="text-white text-2xl drop-shadow-md" />
                                                </div>
                                            </div>

                                            {isLoading && (
                                                <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
                                                    <CircularProgress size={24} sx={{ color: '#10b981' }} />
                                                </div>
                                            )}

                                            {/* Date Badge */}
                                            <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-md px-2 py-1 rounded-md border border-white/10">
                                                <p className="text-[10px] font-medium text-white/90">
                                                    {dataFormatter(video.updatedAt).split(' ').slice(0, 3).join(' ')}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Content Container */}
                                        <div className="p-4 flex flex-col flex-1 gap-3">
                                            {/* Title & Icons */}
                                            <div className="flex justify-between items-start gap-2">
                                                <h3 className="font-bold text-slate-800 line-clamp-1 flex-1 text-base group-hover:text-emerald-700 transition-colors">
                                                    {truncateTitle(extractTitle(video.game[0]?.path))}
                                                </h3>
                                                {video.coachManualFeedback && (
                                                    <div className="text-emerald-500" title="Has feedback">
                                                        <FontAwesomeIcon icon={faComment} size="sm" />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Player Name */}
                                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                                <span className="font-medium text-emerald-600">{extractPlayerName(video)}</span>
                                            </div>

                                            {/* Action Button */}
                                            <div className="mt-auto pt-3 border-t border-gray-50">
                                                <button
                                                    className="w-full py-2 bg-[#15803d] text-white rounded-lg text-sm font-medium hover:bg-[#14532d] transition-colors flex items-center justify-center gap-2 shadow-sm group-hover:shadow-md"
                                                >
                                                    <span>View Report</span>
                                                    <FontAwesomeIcon icon={faCirclePlay} size="xs" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="col-span-full flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm text-center">
                                <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mb-4">
                                    <FontAwesomeIcon icon={faCirclePlay} className="text-3xl text-emerald-200" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">No reviews found</h3>
                                <p className="text-gray-500 mt-2 max-w-sm">
                                    You haven't reviewed any videos yet, or no videos match your current search filters.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* No Search Results Message */}
                {data?.length === 0 && searchTerm && !isStarted && (
                    <div className="mt-8 text-center text-gray-500 bg-white p-8 rounded-xl border border-dashed border-gray-200">
                        <p>No matching videos found for "{searchTerm}"</p>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="mt-8 flex justify-center w-full">
                        <SearchUploadedVideoPagination
                            totalNumberofPages={totalPages}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>

            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="dark"
            />
        </div>
    );
};

export default Reviewd;
